/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import Lottie from 'react-lottie'
import {css} from '@emotion/core'
import {getTheme, getThemeStyles} from '../util/theme'
import {getLanguageCode} from '../util/locale'
import smoothScroll from '../util/smoothScroll'

import Layout from '../components/Layout'
import Hero from '../components/Hero'

import privacyPolicyData from '../translations/privacyPolicyData.json'

const PrivacyPolicy = ({languageCode}) => {
    const lang = languageCode || getLanguageCode()

    const {
        seo,
        heroSubtitle,
        heroDescription,
        sections,
        contentsTitle,
        missionText,
    } = privacyPolicyData[lang]

    const heroBodyHtml = heroDescription.map((p) => `<p>${p}</p>`).join('\n')

    const [theme, setTheme] = useState('light')
    const [themeStyles, setThemeStyles] = useState({})
    const [activeId, setActiveId] = useState('')
    const [headerLottieOptions, setHeaderLottieOptions] = useState({
        loop: true,
        autoplay: true,
    })
    const [missionLottieOptions, setMissionLottieOptions] = useState({
        loop: true,
        autoplay: true,
    })

    const HASH_ROUTE_PATTERN = /#[\w-]+/g
    const getScrollToId = () => {
        if (typeof window === `undefined` && typeof document === `undefined`) {
            return null
        }
        const matched = window.location.href.match(HASH_ROUTE_PATTERN)
        if (matched) return matched[0]
        return ''
    }

    // id should include a # before it
    const scrollToEl = (id) => {
        return (e) => {
            if (e) e.preventDefault()
            const el = document.querySelector(`div[data-scroll-id='${id}']`)
            if (el) {
                setActiveId(id)
                if (typeof window !== `undefined` && typeof document !== 'undefined') {
                    if (window.history.pushState) {
                        window.history.pushState(null, null, id)
                    } else {
                        window.location.hash = id
                    }
                }
                smoothScroll(el, 130)
            }
        }
    }

    const scrollToElOnLoad = () => {
        if (typeof window === `undefined` && typeof document === `undefined`) {
            return null
        }
        const locationHash = getScrollToId()
        scrollToEl(locationHash)()
    }

    useEffect(() => {
        const t = getTheme()
        const tStyles = getThemeStyles()
        setTheme(t)
        setThemeStyles(tStyles)
        if (t === 'dark') {
            import('../images/lottie/privacyHeaderDark.json').then((data) => {
                const {default: dataHeader} = data
                setHeaderLottieOptions((opts) => ({
                    ...opts,
                    animationData: dataHeader,
                }))
            })
            import('../images/lottie/privacyMissionDark.json').then((data) => {
                const {default: dataMission} = data
                setMissionLottieOptions((opts) => ({
                    ...opts,
                    animationData: dataMission,
                }))
            })
        } else {
            import('../images/lottie/privacyHeader.json').then((data) => {
                const {default: dataHeader} = data
                setHeaderLottieOptions((opts) => ({
                    ...opts,
                    animationData: dataHeader,
                }))
            })
            import('../images/lottie/privacyMission.json').then((data) => {
                const {default: dataMission} = data
                setMissionLottieOptions((opts) => ({
                    ...opts,
                    animationData: dataMission,
                }))
            })
        }
    }, [])

    useEffect(() => {
        scrollToElOnLoad()
    }, [scrollToElOnLoad])

    return (
        <Layout
            title={seo.title}
            description={seo.description}
            lang={lang}
            canonicalPath="/privacy-policy/"
            themeStyles={themeStyles}
            theme={theme}
        >
            <Hero
                subTitle=""
                title={heroSubtitle}
                lottieOptions={headerLottieOptions}
                body={heroBodyHtml}
                theme={theme}
                themeStyles={themeStyles}
                overrideStyles={`
                    .text {
                        h2 {
                            color: ${themeStyles.emphasisTextColor} !important;
                        }
                        @media (max-width: 990px) {
                            h2 {
                                font-size: ${languageCode === 'de' ? '27px' : '35px'};
                            }
                        }
                        @media *max-width: 900px {
                            padding: 0;
                        }
                    }
                `}
            />

            <section
                css={css`
                    padding: 4rem 2rem;
                    background: ${themeStyles.privacyBackground};
                    h1,
                    h2,
                    h3,
                    p,
                    strong,
                    a,
                    b,
                    em,
                    span,
                    ul,
                    li,
                    table,
                    th,
                    td {
                        color: ${themeStyles.primaryTextColor};
                    }
                    a,
                    a span {
                        color: ${themeStyles.emphasisTextColor};
                    }
                `}
            >
                <div
                    className="inner"
                    css={css`
                        margin: auto;
                        display: flex;
                        max-width: 1200px;
                    `}
                >
                    <div
                        className="contents"
                        css={css`
                            flex: 1;
                            border-radius: 4px;
                            box-shadow: 0px 10px 20px rgba(32, 41, 69, 0.1);
                            height: 100%;
                            padding: 0;
                            margin: 1rem 2.5rem 0 0;
                            max-width: 315px;
                            position: -webkit-sticky;
                            position: sticky;
                            top: 125px;
                            background: ${themeStyles.lighterBackgroundColor};
                            h2 {
                                color: ${themeStyles.emphasisTextColor};
                                font-size: 30px;
                                font-weight: 600;
                                line-height: 32px;
                                position: absolute;
                                z-index: 3;
                                top: -1rem;
                                left: 1rem;
                            }
                            @media (max-width: 990px) {
                                display: none;
                            }
                        `}
                    >
                        <h2>{contentsTitle}</h2>
                        <div
                            css={css`
                                padding: 1.5rem 0.5rem 1rem 0.5rem;
                                background: ${themeStyles.lighterBackgroundColor};
                                border-radius: 4px;
                                position: relative;
                                z-index: 2;
                            `}
                        >
                            {sections.map(({title}, i) => {
                                const id = `#section-${i + 1}`
                                return (
                                    <div
                                        key={id}
                                        css={css`
                                            &:not(:last-of-type) {
                                                margin-bottom: 8px;
                                            }
                                        `}
                                    >
                                        <div
                                            css={css`
                                                ${id === activeId
                                                    ? `
                                                            background: ${themeStyles.privacyTabHighlight};
                                                            span {
                                                                font-weight: 600;
                                                                color: ${themeStyles.emphasisTextColor};
                                                            }
                                                        `
                                                    : ``}
                                                padding: 4px 8px;
                                                border-radius: 4px;
                                                font-size: 1rem;
                                                line-height: 24px;
                                                &:hover {
                                                    cursor: pointer;
                                                    span {
                                                        color: ${themeStyles.emphasisTextColor};
                                                    }
                                                }
                                                &:focus {
                                                    outline: none;
                                                }
                                            `}
                                            onClick={scrollToEl(id)}
                                            onKeyUp={scrollToEl(id)}
                                            role="tab"
                                            aria-selected="true"
                                            aria-controls="tabpanel-id"
                                            id="tab-id"
                                            tabIndex="0"
                                        >
                                            <span>{title}</span>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                        <div
                            css={css`
                                width: 40px;
                                height: 40px;
                                position: absolute;
                                z-index: 3;
                                right: 30px;
                                top: -30px;
                                background-color: ${themeStyles.privacyBoxBackground};
                                border: solid 1px ${themeStyles.privacyBoxBorder};
                            `}
                        />
                        <div
                            css={css`
                                width: 64px;
                                height: 64px;
                                position: absolute;
                                z-index: 1;
                                left: -40px;
                                bottom: -40px;
                                background-color: transparent;
                                border: solid 2px ${themeStyles.brandBlue};
                            `}
                        />
                    </div>
                    <div
                        className="privacy-policy"
                        css={css`
                            flex: 1;
                            h3 {
                                margin: 2.5rem 0 1rem;
                                font-size: 24px;
                                font-weight: 600;
                                line-height: 32px;
                            }
                            .privacy-policy-item:first-of-type {
                                h3 {
                                    margin: 0 0 1rem;
                                }
                            }
                            p,
                            li {
                                line-height: 24px;
                                margin: 0 0 1rem;
                                font-size: 16px;
                            }
                            ul {
                                padding: 0 0 0 1.5rem;
                            }
                            li {
                                &::marker {
                                    color: ${themeStyles.emphasisTextColor};
                                }
                                ul {
                                    padding: 0;
                                    li {
                                        list-style-type: none;
                                    }
                                }
                            }
                            a {
                                color: ${themeStyles.emphasisTextColor};
                                text-decoration: none;
                            }
                            .privacy-policy-table-wrap {
                                overflow-x: auto;
                                margin: 0 0 1.5rem;
                            }
                            table {
                                width: 100%;
                                border-collapse: collapse;
                                font-size: 14px;
                                line-height: 20px;
                            }
                            th,
                            td {
                                border: 1px solid ${themeStyles.borderColor};
                                padding: 8px 10px;
                                text-align: left;
                                vertical-align: top;
                            }
                            th {
                                background: ${themeStyles.privacyTabHighlight};
                                font-weight: 600;
                            }
                        `}
                    >
                        {sections.map((section, i) => {
                            const id = `section-${i + 1}`
                            return (
                                <div data-scroll-id={`#${id}`} key={i} className="privacy-policy-item">
                                    <h3>{section.title}</h3>
                                    {section.body.map((para, j) => {
                                        const isBlock = /^\s*<(ul|ol|table|div)\b/i.test(para)
                                        if (isBlock) {
                                            return (
                                                // eslint-disable-next-line react/no-danger
                                                <div key={j} dangerouslySetInnerHTML={{__html: para}} />
                                            )
                                        }
                                        return (
                                            // eslint-disable-next-line react/no-danger
                                            <p key={j} dangerouslySetInnerHTML={{__html: para}} />
                                        )
                                    })}
                                    {section.bulletPoints && (
                                        <>
                                            <p><strong>{section.subHeading}</strong></p>
                                            <p>{section.clarification}</p>
                                            <ul>
                                                {section.bulletPoints.map((bp, k) => (
                                                    <li key={k}>
                                                        <strong>{bp.heading}</strong>
                                                        <ul>
                                                            <li>{bp.detail}</li>
                                                        </ul>
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>
            </section>

            <div
                css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 4rem 2rem 3rem;
                    background: ${themeStyles.lighterBackgroundColor};
                    text-align: center;
                    h2 {
                        max-width: 550px;
                        font-size: 30px;
                        font-weight: 600;
                        line-height: 40px;
                        margin: 2rem 0;
                        color: ${themeStyles.primaryTextColor};
                    }
                `}
            >
                <div
                    css={css`
                        max-width: 308px;
                        height: auto;
                        margin: auto;
                        width: 100%;
                    `}
                >
                    <Lottie
                        css={css`
                            width: 100%;
                            height: 100%;
                        `}
                        options={missionLottieOptions}
                    />
                </div>
                <h2>{missionText}</h2>
            </div>
        </Layout>
    )
}

PrivacyPolicy.propTypes = {
    languageCode: PropTypes.string,
}

export default PrivacyPolicy
